import type {
  AuditEvent,
  ClaudeConnectorGrant,
  DeploymentJob,
  DeploymentReceipt,
  EditorCapture,
  EditorState,
  ForgeServer,
  Location,
  MinecraftAccountLink,
  Json,
  Npc,
  NpcDeletionBackup,
  NpcRelease,
  NpcStatus,
  NpcVersion,
  Season,
  ServerCapability,
  ServerHealthSnapshot,
  ServerStateSnapshot,
  StaffMember,
  Workspace,
  WorkspaceCapabilityGrant,
  WorldDecisionReceipt,
  WorldProfileManifestV1,
  WorldProfileManifestV2,
  WorldProfileManifestV3,
  GuildStateSnapshot,
  DailyContractStateSnapshot,
  PlayerProgressModerationReceipt,
  WorldProfileCapture,
  WorldProfileRecord,
  WorldRelease,
  WorldStateSnapshot,
} from "../types/domain";
import { normalizeEditorState } from "../migrations/editorState";
import { supabase } from "./client";

function client() {
  if (!supabase)
    throw new Error(
      "Supabase is not configured. Add the required Vite environment variables.",
    );
  return supabase;
}
function unwrap<T>(result: {
  data: T | null;
  error: { message: string; code?: string } | null;
}): T {
  if (result.error) throw new Error(result.error.message);
  if (result.data == null) throw new Error("Supabase returned no data.");
  return result.data;
}
function unwrapSingleRpcRow<T>(result: {
  data: T | T[] | null;
  error: { message: string; code?: string } | null;
}): T {
  const data = unwrap(result);
  if (!Array.isArray(data)) return data;
  if (data.length !== 1)
    throw new Error(
      `Supabase returned ${data.length} rows where exactly one was expected.`,
    );
  return data[0];
}
function unwrapOneTimeCode(result: {
  data:
    | { code?: unknown; expires_at?: unknown }
    | { code?: unknown; expires_at?: unknown }[]
    | null;
  error: { message: string; code?: string } | null;
}): { code: string; expires_at: string } {
  const row = unwrapSingleRpcRow(result);
  if (
    typeof row.code !== "string" ||
    !row.code ||
    typeof row.expires_at !== "string" ||
    !row.expires_at
  )
    throw new Error("Supabase returned an invalid one-time code response.");
  return { code: row.code, expires_at: row.expires_at };
}
function normalizeNpc(npc: Npc): Npc {
  return { ...npc, editor_state: normalizeEditorState(npc.editor_state) };
}

export interface WorkspaceBundle {
  workspace: Workspace;
  member: StaffMember;
  staff: StaffMember[];
  seasons: Season[];
  locations: Location[];
  npcs: Npc[];
}

export async function loadWorkspaceBundle(): Promise<WorkspaceBundle> {
  const db = client();
  const { data: auth } = await db.auth.getUser();
  if (!auth.user) throw new Error("Your staff session has expired.");
  const membership = unwrap(
    await db
      .from("workspace_members")
      .select("*")
      .eq("user_id", auth.user.id)
      .eq("active", true)
      .limit(1)
      .maybeSingle(),
  ) as any;
  const workspaceId = membership.workspace_id;
  const [workspace, profile, staffRows, profiles, seasons, locations, npcs] =
    await Promise.all([
      db.from("workspaces").select("*").eq("id", workspaceId).single(),
      db
        .from("staff_profiles")
        .select("*")
        .eq("user_id", auth.user.id)
        .single(),
      db
        .from("workspace_members")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("creator_number"),
      db.from("staff_profiles").select("*"),
      db
        .from("seasons")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("sort_order")
        .order("season_number"),
      db
        .from("locations")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("sort_order")
        .order("name"),
      db
        .from("npcs")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("updated_at", { ascending: false }),
    ]);
  const currentProfile = unwrap(profile) as any;
  const currentMember = {
    ...membership,
    display_name: currentProfile.display_name,
    email: currentProfile.email,
  } as StaffMember;
  const profileRows = unwrap(profiles as any) as any[];
  const staff = (unwrap(staffRows as any) as any[]).map((row: any) => {
    const person = profileRows.find((item) => item.user_id === row.user_id);
    return {
      ...row,
      display_name: person?.display_name || "Staff",
      email: person?.email || "",
    };
  });
  return {
    workspace: unwrap(workspace) as Workspace,
    member: currentMember,
    staff,
    seasons: unwrap(seasons as any) as Season[],
    locations: unwrap(locations as any) as Location[],
    npcs: (unwrap(npcs as any) as Npc[]).map(normalizeNpc),
  };
}

export interface CreateNpcInput {
  workspaceId: string;
  seasonId: string;
  locationId: string | null;
  name: string;
  role: string;
  notes: string;
}
export async function createNpc(input: CreateNpcInput): Promise<Npc> {
  const result = await client().rpc("create_npc", {
    target_workspace: input.workspaceId,
    target_season: input.seasonId,
    target_location: input.locationId,
    npc_name: input.name,
    npc_role: input.role,
    npc_notes: input.notes,
  });
  return normalizeNpc(unwrap(result as any) as Npc);
}
export async function duplicateNpc(id: string): Promise<Npc> {
  return normalizeNpc(
    unwrap(
      (await client().rpc("duplicate_npc", { target_npc: id })) as any,
    ) as Npc,
  );
}

export interface SaveNpcInput {
  npc: Npc;
  loadedRevision: number;
  state: EditorState;
  compiled: string;
  versionLabel?: string | null;
  automatic?: boolean;
}
export async function saveNpc({
  npc,
  loadedRevision,
  state,
  compiled,
  versionLabel = null,
  automatic = true,
}: SaveNpcInput): Promise<Npc> {
  const result = await client().rpc("save_npc", {
    target_npc: npc.id,
    expected_revision: loadedRevision,
    next_editor_state: state,
    next_compiled_dsc: compiled,
    next_name: npc.name,
    next_script_id: npc.script_id,
    next_citizens_id: npc.citizens_id,
    next_role: npc.role,
    next_status: npc.status,
    next_notes: npc.notes,
    next_season: npc.season_id,
    next_location: npc.location_id,
    version_label: versionLabel,
    automatic_version: automatic,
  });
  if (
    result.error?.message.includes("revision_conflict") ||
    result.error?.code === "40001"
  )
    throw Object.assign(
      new Error("Remote version changed while you were editing."),
      { code: "REVISION_CONFLICT" },
    );
  return normalizeNpc(unwrap(result as any) as Npc);
}

export async function updateNpcStatus(
  npc: Npc,
  status: NpcStatus,
): Promise<Npc> {
  return saveNpc({
    npc: { ...npc, status },
    loadedRevision: npc.revision,
    state: npc.editor_state,
    compiled: npc.compiled_dsc,
    versionLabel: `${status} status`,
    automatic: false,
  });
}

export async function getNpc(id: string): Promise<Npc> {
  return normalizeNpc(
    unwrap(
      await client().from("npcs").select("*").eq("id", id).single(),
    ) as Npc,
  );
}
export async function getNpcVersions(id: string): Promise<NpcVersion[]> {
  return unwrap(
    await client()
      .from("npc_versions")
      .select("*")
      .eq("npc_id", id)
      .order("created_at", { ascending: false }),
  ) as NpcVersion[];
}
export async function restoreNpcVersion(id: string): Promise<Npc> {
  return normalizeNpc(
    unwrap(
      (await client().rpc("restore_npc_version", {
        target_version: id,
      })) as any,
    ) as Npc,
  );
}
export async function getAuditLog(
  workspaceId: string,
  npcId?: string,
): Promise<AuditEvent[]> {
  let query = client()
    .from("audit_log")
    .select("*")
    .eq("workspace_id", workspaceId)
    .order("created_at", { ascending: false })
    .limit(100);
  if (npcId) query = query.eq("npc_id", npcId);
  return unwrap(await query) as AuditEvent[];
}

export async function createSeason(
  workspaceId: string,
  number: number,
  name: string,
  description = "",
): Promise<Season> {
  return unwrap(
    (await client().rpc("create_season", {
      target_workspace: workspaceId,
      target_number: number,
      target_name: name,
      target_description: description,
    })) as any,
  ) as Season;
}
export async function updateSeason(
  season: Season,
  patch: Pick<Season, "name" | "description" | "sort_order" | "archived">,
): Promise<Season> {
  return unwrap(
    (await client().rpc("update_season", {
      target_season: season.id,
      next_name: patch.name,
      next_description: patch.description,
      next_sort_order: patch.sort_order,
      next_archived: patch.archived,
    })) as any,
  ) as Season;
}
export async function createLocation(
  workspaceId: string,
  seasonId: string,
  name: string,
  parentId: string | null,
  description = "",
): Promise<Location> {
  return unwrap(
    (await client().rpc("create_location", {
      target_workspace: workspaceId,
      target_season: seasonId,
      target_name: name,
      target_parent: parentId,
      target_description: description,
    })) as any,
  ) as Location;
}
export async function updateLocation(
  location: Location,
  patch: Pick<
    Location,
    "name" | "parent_id" | "description" | "sort_order" | "archived"
  >,
): Promise<Location> {
  return unwrap(
    (await client().rpc("update_location", {
      target_location: location.id,
      next_name: patch.name,
      next_parent: patch.parent_id,
      next_description: patch.description,
      next_sort_order: patch.sort_order,
      next_archived: patch.archived,
    })) as any,
  ) as Location;
}
export async function bulkMoveNpcs(
  ids: string[],
  seasonId: string,
  locationId: string | null,
): Promise<Npc[]> {
  return unwrap(
    (await client().rpc("bulk_move_npcs", {
      target_ids: ids,
      target_season: seasonId,
      target_location: locationId,
    })) as any,
  ) as Npc[];
}
export async function bulkSetNpcStatus(
  ids: string[],
  status: NpcStatus,
): Promise<Npc[]> {
  return unwrap(
    (await client().rpc("bulk_set_npc_status", {
      target_ids: ids,
      target_status: status,
    })) as any,
  ) as Npc[];
}
export async function deleteNpc(
  id: string,
  expectedCode: string,
): Promise<NpcDeletionBackup> {
  return unwrap(
    (await client().rpc("delete_npc", {
      target_npc: id,
      expected_code: expectedCode,
    })) as any,
  ) as NpcDeletionBackup;
}
export async function updateMember(
  workspaceId: string,
  userId: string,
  patch: Partial<Pick<StaffMember, "creator_number" | "role" | "active">>,
): Promise<void> {
  const { error } = await client()
    .from("workspace_members")
    .update(patch)
    .eq("workspace_id", workspaceId)
    .eq("user_id", userId);
  if (error) throw new Error(error.message);
}
export async function addWorkspaceMember(
  workspaceId: string,
  email: string,
  displayName: string,
  creatorNumber: number,
  role: StaffMember["role"],
): Promise<void> {
  const { error } = await client().rpc("add_workspace_member", {
    target_workspace: workspaceId,
    target_email: email,
    target_display_name: displayName,
    target_creator_number: creatorNumber,
    target_role: role,
  });
  if (error) throw new Error(error.message);
}

export interface ForgeRuntimeOverview {
  servers: ForgeServer[];
  capabilities: ServerCapability[];
  grants: WorkspaceCapabilityGrant[];
  accountLinks: MinecraftAccountLink[];
  jobs: DeploymentJob[];
  receipts: DeploymentReceipt[];
  snapshots: ServerStateSnapshot[];
  health: ServerHealthSnapshot[];
  stats: { syncBacklog: number };
}

export async function getForgeRuntimeOverview(
  workspaceId: string,
): Promise<ForgeRuntimeOverview> {
  const db = client();
  const [
    servers,
    capabilities,
    grants,
    accountLinks,
    jobs,
    receipts,
    snapshots,
    health,
    backlog,
  ] = await Promise.all([
    db
      .from("forge_servers")
      .select("*")
      .eq("workspace_id", workspaceId)
      .order("created_at"),
    db
      .from("server_capabilities")
      .select("*")
      .eq("workspace_id", workspaceId)
      .order("capability"),
    db
      .from("workspace_capability_grants")
      .select("*")
      .eq("workspace_id", workspaceId),
    db
      .from("minecraft_account_links")
      .select("*")
      .eq("workspace_id", workspaceId)
      .order("created_at", { ascending: false }),
    db
      .from("deployment_jobs")
      .select("*")
      .eq("workspace_id", workspaceId)
      .order("created_at", { ascending: false })
      .limit(50),
    db
      .from("deployment_receipts")
      .select("*")
      .eq("workspace_id", workspaceId)
      .order("completed_at", { ascending: false })
      .limit(50),
    db
      .from("server_state_snapshots")
      .select("*")
      .eq("workspace_id", workspaceId)
      .order("captured_at", { ascending: false })
      .limit(50),
    db
      .from("server_health_snapshots")
      .select("*")
      .eq("workspace_id", workspaceId)
      .order("captured_at", { ascending: false })
      .limit(50),
    db
      .from("runtime_sync_inbox")
      .select("*", { count: "exact", head: true })
      .eq("workspace_id", workspaceId)
      .is("completed_at", null),
  ]);
  for (const result of [
    servers,
    capabilities,
    grants,
    accountLinks,
    jobs,
    receipts,
    snapshots,
    health,
    backlog,
  ])
    if (result.error) throw new Error(result.error.message);
  return {
    servers: servers.data as ForgeServer[],
    capabilities: capabilities.data as ServerCapability[],
    grants: grants.data as WorkspaceCapabilityGrant[],
    accountLinks: accountLinks.data as MinecraftAccountLink[],
    jobs: jobs.data as DeploymentJob[],
    receipts: receipts.data as DeploymentReceipt[],
    snapshots: snapshots.data as ServerStateSnapshot[],
    health: health.data as ServerHealthSnapshot[],
    stats: { syncBacklog: backlog.count || 0 },
  };
}

export async function createServerPairing(
  workspaceId: string,
  serverName: string,
): Promise<{ code: string; expires_at: string }> {
  return unwrapOneTimeCode(
    (await client().rpc("create_server_pairing", {
      target_workspace: workspaceId,
      server_name: serverName,
    })) as any,
  );
}

export async function revokeForgeServer(serverId: string): Promise<void> {
  const result = await client().rpc("revoke_forge_server", {
    target_server: serverId,
  });
  if (result.error) throw new Error(result.error.message);
}

export async function rotateForgeServer(
  serverId: string,
): Promise<{ code: string; expires_at: string }> {
  return unwrapOneTimeCode(
    (await client().rpc("rotate_forge_server_pairing", {
      target_server: serverId,
    })) as any,
  );
}

export async function setCapabilityGrant(
  workspaceId: string,
  userId: string,
  capability: "deploy",
  enabled: boolean,
): Promise<void> {
  const result = await client().rpc("set_workspace_capability_grant", {
    target_workspace: workspaceId,
    target_user: userId,
    target_capability: capability,
    enabled,
  });
  if (result.error) throw new Error(result.error.message);
}

export async function getClaudeConnectorGrants(workspaceId:string):Promise<ClaudeConnectorGrant[]>{
  const result=await client().from('claude_connector_grants').select('*').eq('workspace_id',workspaceId).order('updated_at',{ascending:false})
  if(result.error)throw new Error(result.error.message)
  return result.data as ClaudeConnectorGrant[]
}

export async function canManageClaudeConnector(workspaceId:string):Promise<boolean>{
  const result=await client().rpc('is_claude_connector_administrator',{target_workspace:workspaceId})
  if(result.error)throw new Error(result.error.message)
  return result.data===true
}

export async function registerClaudeConnectorGrant(
  workspaceId:string,
  clientId:string,
  clientName:string,
  allowWrite:boolean,
  allowDeploy:boolean,
):Promise<ClaudeConnectorGrant>{
  return unwrap((await client().rpc('register_claude_connector_grant',{
    target_workspace:workspaceId,
    target_client_id:clientId,
    target_client_name:clientName,
    write_allowed:allowWrite,
    deploy_allowed:allowDeploy,
  })) as any) as ClaudeConnectorGrant
}

export async function updateClaudeConnectorGrant(
  grantId:string,
  allowWrite:boolean,
  allowDeploy:boolean,
  active:boolean,
):Promise<ClaudeConnectorGrant>{
  return unwrap((await client().rpc('update_claude_connector_grant',{
    target_grant:grantId,
    write_allowed:allowWrite,
    deploy_allowed:allowDeploy,
    next_active:active,
  })) as any) as ClaudeConnectorGrant
}

export async function approveMinecraftAccountLink(
  linkId: string,
  approved: boolean,
): Promise<void> {
  const result = await client().rpc("approve_minecraft_account_link", {
    target_link: linkId,
    approved,
  });
  if (result.error) throw new Error(result.error.message);
}

export async function createMinecraftAccountLinkCode(
  workspaceId: string,
): Promise<{ code: string; expires_at: string }> {
  return unwrapOneTimeCode(
    (await client().rpc("create_minecraft_account_link_code", {
      target_workspace: workspaceId,
    })) as any,
  );
}

export async function consumeForgeEditorSession(
  sessionCode: string,
  npcId: string,
): Promise<string> {
  return unwrap(
    (await client().rpc("consume_forge_editor_session", {
      session_code: sessionCode,
      target_npc: npcId,
    })) as any,
  ) as string;
}

export async function getEditorCaptures(
  npcId: string,
): Promise<EditorCapture[]> {
  return unwrap(
    (await client()
      .from("editor_session_captures")
      .select("*")
      .eq("npc_id", npcId)
      .is("consumed_at", null)
      .order("created_at", { ascending: false })
      .limit(30)) as any,
  ) as EditorCapture[];
}

export async function markEditorCaptureConsumed(
  captureId: string,
): Promise<void> {
  const result = await client()
    .from("editor_session_captures")
    .update({ consumed_at: new Date().toISOString() })
    .eq("id", captureId);
  if (result.error) throw new Error(result.error.message);
}

export async function stageNpcRelease(
  npcId: string,
  serverId: string,
  expectedRevision: number,
  confirmAdoption = false,
): Promise<{ release: NpcRelease; job: DeploymentJob }> {
  const { data, error } = await client().functions.invoke("stage-release", {
    body: { npcId, serverId, expectedRevision, confirmAdoption },
  });
  if (error) throw new Error(error.message);
  if (!data?.release || !data?.job)
    throw new Error("The release service returned an incomplete response.");
  return data as { release: NpcRelease; job: DeploymentJob };
}

export async function approveDeploymentJob(
  jobId: string,
  acknowledgedExperimental: string[] = [],
): Promise<DeploymentJob> {
  const { data, error } = await client().functions.invoke(
    "approve-deployment",
    { body: { jobId, acknowledgedExperimental } },
  );
  if (error) throw new Error(error.message);
  if (!data?.job)
    throw new Error("The approval service returned an incomplete response.");
  return data.job as DeploymentJob;
}

export async function requestRollback(
  serverId: string,
  npcId: string,
): Promise<DeploymentJob> {
  const { data, error } = await client().functions.invoke("request-rollback", {
    body: { serverId, npcId },
  });
  if (error) throw new Error(error.message);
  if (!data?.job)
    throw new Error("The rollback service returned an incomplete response.");
  return data.job as DeploymentJob;
}

export async function getNpcDeploymentHistory(
  npcId: string,
): Promise<{
  jobs: DeploymentJob[];
  releases: NpcRelease[];
  receipts: DeploymentReceipt[];
}> {
  const db = client();
  const [jobs, releases] = await Promise.all([
    db
      .from("deployment_jobs")
      .select("*")
      .eq("npc_id", npcId)
      .order("created_at", { ascending: false })
      .limit(20),
    db
      .from("npc_releases")
      .select("*")
      .eq("npc_id", npcId)
      .order("created_at", { ascending: false })
      .limit(20),
  ]);
  if (jobs.error) throw new Error(jobs.error.message);
  if (releases.error) throw new Error(releases.error.message);
  const releaseIds = (releases.data || []).map((release) => release.id);
  if (!releaseIds.length)
    return { jobs: jobs.data as DeploymentJob[], releases: [], receipts: [] };
  const receipts = await db
    .from("deployment_receipts")
    .select("*")
    .in("release_id", releaseIds)
    .order("completed_at", { ascending: false })
    .limit(20);
  if (receipts.error) throw new Error(receipts.error.message);
  return {
    jobs: jobs.data as DeploymentJob[],
    releases: releases.data as NpcRelease[],
    receipts: receipts.data as DeploymentReceipt[],
  };
}

export async function getNpcBinding(serverId:string,npcId:string){
  const db=client();const [link,report]=await Promise.all([
    db.from('npc_server_links').select('*').eq('server_id',serverId).eq('npc_id',npcId).maybeSingle(),
    db.from('server_npc_binding_reports').select('*').eq('server_id',serverId).eq('npc_id',npcId).maybeSingle(),
  ]);if(link.error)throw new Error(link.error.message);if(report.error)throw new Error(report.error.message);let expected=null;if(link.data?.deployed_release_id){const release=await db.from('npc_releases').select('*').eq('id',link.data.deployed_release_id).maybeSingle();if(release.error)throw new Error(release.error.message);expected=release.data}return {link:link.data,report:report.data,expected}
}

export async function getNpcRuntime(
  npcId: string,
): Promise<{
  releases: NpcRelease[];
  jobs: DeploymentJob[];
  snapshots: ServerStateSnapshot[];
  captures: EditorCapture[];
}> {
  const db = client();
  const [releases, jobs, snapshots, captures] = await Promise.all([
    db
      .from("npc_releases")
      .select("*")
      .eq("npc_id", npcId)
      .order("created_at", { ascending: false })
      .limit(20),
    db
      .from("deployment_jobs")
      .select("*")
      .eq("npc_id", npcId)
      .order("created_at", { ascending: false })
      .limit(20),
    db
      .from("server_state_snapshots")
      .select("*")
      .eq("npc_id", npcId)
      .order("captured_at", { ascending: false })
      .limit(20),
    db
      .from("editor_session_captures")
      .select("*")
      .eq("npc_id", npcId)
      .is("consumed_at", null)
      .order("created_at", { ascending: false })
      .limit(30),
  ]);
  for (const result of [releases, jobs, snapshots, captures])
    if (result.error) throw new Error(result.error.message);
  return {
    releases: releases.data as NpcRelease[],
    jobs: jobs.data as DeploymentJob[],
    snapshots: snapshots.data as ServerStateSnapshot[],
    captures: captures.data as EditorCapture[],
  };
}

export interface WorldConsoleData {
  profile: WorldProfileRecord | null;
  releases: WorldRelease[];
  snapshots: WorldStateSnapshot[];
  decisions: WorldDecisionReceipt[];
  jobs: DeploymentJob[];
  receipts: DeploymentReceipt[];
  captures: WorldProfileCapture[];
  guildSnapshots: GuildStateSnapshot[];
  dailyContractSnapshots: DailyContractStateSnapshot[];
  progressModerationReceipts: PlayerProgressModerationReceipt[];
}
export async function getWorldConsole(
  serverId: string,
): Promise<WorldConsoleData> {
  const db = client();
  const profileResult = await db
    .from("world_profiles")
    .select("*")
    .eq("server_id", serverId)
    .maybeSingle();
  if (profileResult.error) throw new Error(profileResult.error.message);
  const profile = profileResult.data as WorldProfileRecord | null;
  const capturesResult = await db
    .from("world_profile_captures")
    .select("*")
    .eq("server_id", serverId)
    .is("consumed_at", null)
    .order("created_at", { ascending: false })
    .limit(100);
  if (capturesResult.error) throw new Error(capturesResult.error.message);
  if (!profile)
    return {
      profile: null,
      releases: [],
      snapshots: [],
      decisions: [],
      jobs: [],
      receipts: [],
      captures: capturesResult.data as WorldProfileCapture[],
      guildSnapshots: [],
      dailyContractSnapshots: [],
      progressModerationReceipts: [],
    };
  const [releases, snapshots, decisions, jobs, receipts, guildSnapshots, dailyContractSnapshots, progressModerationReceipts] = await Promise.all([
    db
      .from("world_releases")
      .select("*")
      .eq("server_id", serverId)
      .order("created_at", { ascending: false })
      .limit(20),
    db
      .from("world_state_snapshots")
      .select("*")
      .eq("server_id", serverId)
      .order("captured_at", { ascending: false })
      .limit(20),
    db
      .from("world_decision_receipts")
      .select("*")
      .eq("server_id", serverId)
      .order("occurred_at", { ascending: false })
      .limit(100),
    db
      .from("deployment_jobs")
      .select("*")
      .eq("server_id", serverId)
      .eq("target_type", "world_profile")
      .order("created_at", { ascending: false })
      .limit(20),
    db
      .from("deployment_receipts")
      .select("*")
      .eq("server_id", serverId)
      .not("world_release_id", "is", null)
      .order("completed_at", { ascending: false })
      .limit(20),
    db.from("guild_state_snapshots").select("*").eq("server_id",serverId).order("captured_at",{ascending:false}).limit(20),
    db.from("daily_contract_state_snapshots").select("*").eq("server_id",serverId).order("captured_at",{ascending:false}).limit(20),
    db.from("player_progress_moderation_receipts").select("*").eq("server_id",serverId).order("received_at",{ascending:false}).limit(50),
  ]);
  for (const result of [releases, snapshots, decisions, jobs, receipts, guildSnapshots, dailyContractSnapshots, progressModerationReceipts])
    if (result.error) throw new Error(result.error.message);
  return {
    profile,
    releases: releases.data as WorldRelease[],
    snapshots: snapshots.data as WorldStateSnapshot[],
    decisions: decisions.data as WorldDecisionReceipt[],
    jobs: jobs.data as DeploymentJob[],
    receipts: receipts.data as DeploymentReceipt[],
    captures: capturesResult.data as WorldProfileCapture[],
    guildSnapshots: guildSnapshots.data as GuildStateSnapshot[],
    dailyContractSnapshots: dailyContractSnapshots.data as DailyContractStateSnapshot[],
    progressModerationReceipts: progressModerationReceipts.data as PlayerProgressModerationReceipt[],
  };
}
export async function consumeWorldCapture(captureId: string): Promise<void> {
  const { error } = await client()
    .from("world_profile_captures")
    .update({ consumed_at: new Date().toISOString() })
    .eq("id", captureId);
  if (error) throw new Error(error.message);
}
export async function saveWorldProfile(
  serverId: string,
  profile: WorldProfileManifestV1|WorldProfileManifestV2|WorldProfileManifestV3,
): Promise<WorldProfileRecord> {
  return unwrap(
    (await client().rpc("save_world_profile", {
      target_server: serverId,
      next_profile: profile,
    })) as any,
  ) as WorldProfileRecord;
}
export async function rolloverGuildSeason(serverId:string,closingSeason:string,nextSeason:number,nextName:string,confirmation:string,closingExport:Record<string,Json>){return unwrap((await client().rpc("rollover_guild_season",{target_server:serverId,closing_season:closingSeason,next_season:nextSeason,next_name:nextName,confirmation,closing_export:closingExport})) as any)}
export async function setWorldProfilePaused(
  serverId: string,
  paused: boolean,
): Promise<WorldProfileRecord> {
  return unwrap(
    (await client().rpc("set_world_profile_paused", {
      target_server: serverId,
      paused,
    })) as any,
  ) as WorldProfileRecord;
}
export async function stageWorldRelease(serverId: string): Promise<DeploymentJob> {
  const { data, error } = await client().functions.invoke("stage-world-release", {
    body: { serverId },
  });
  if (error) throw new Error(error.message);
  if (!data?.job) throw new Error("The staging service returned an incomplete response.");
  return data.job as DeploymentJob;
}
export async function requestWorldRollback(serverId: string): Promise<DeploymentJob> {
  return unwrap(
    (await client().rpc("request_world_rollback", { target_server: serverId })) as any,
  ) as DeploymentJob;
}
export interface AiRetirementExport { export_id: string; confirmation_code: string; expires_at: string; encrypted_payload: Record<string, Json> }
export async function createAiRetirementExport(workspaceId: string): Promise<AiRetirementExport> {
  return unwrap((await client().rpc("create_npcforge_ai_retirement_export", { target_workspace: workspaceId })) as any) as AiRetirementExport;
}
export async function confirmAiRetirementPurge(exportId: string, confirmationCode: string): Promise<Record<string, Json>> {
  return unwrap((await client().rpc("confirm_npcforge_ai_purge", { target_export: exportId, confirmation_code: confirmationCode })) as any) as Record<string, Json>;
}
